import "@/components/Interface/SettingToggle/SettingToggle.scss";

import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Toggle } from "@/components/Interface/Toggle/Toggle";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { useAlert } from "@/hooks/useAlert";
import { useChangeMagicLinkPermission, useGetCustomer } from "@/services/api";
import { AlertTypes, userAccountStateAtom } from "@/utils/atoms";
import {
  isMagicLoginNotAllowedError,
  magicLinkErrorKeys,
} from "@/utils/magicLinkErrors";
import { getMagicLoginEnabled } from "@/utils/magicLoginPermission";

export const MagicLinkLoginSetting = () => {
  const { t } = useTranslation("portal");
  const { t: tMagicLinkError } = useTranslation();
  const showAlert = useAlert();
  const userAccountState = useAtomValue(userAccountStateAtom);
  const [optimisticEnabled, setOptimisticEnabled] = useState<boolean | null>(
    null,
  );

  const {
    data: customer,
    isLoading: isLoadingCustomer,
    refetch,
  } = useGetCustomer({ expanded: true });

  const { mutate: changeMagicLoginPermission, isLoading: isUpdating } =
    useChangeMagicLinkPermission({
      mutation: {
        onSuccess: async () => {
          await refetch();
          setOptimisticEnabled(null);
          showAlert({
            type: AlertTypes.success,
            text: t("user-account.security.magicLink.saveSuccess"),
          });
        },
        onError: (error) => {
          setOptimisticEnabled(null);

          showAlert({
            type: AlertTypes.error,
            text: isMagicLoginNotAllowedError(error)
              ? tMagicLinkError(magicLinkErrorKeys.notAllowed)
              : t("user-account.security.magicLink.saveError"),
          });
        },
      },
    });

  useEffect(() => {
    setOptimisticEnabled(null);
  }, [customer]);

  const isEnabled = optimisticEnabled ?? getMagicLoginEnabled(customer);
  const isDisabled =
    isLoadingCustomer ||
    isUpdating ||
    userAccountState.editingMail ||
    userAccountState.editingPassword;

  const handleToggle = () => {
    if (isDisabled) {
      return;
    }

    const nextEnabled = !isEnabled;
    setOptimisticEnabled(nextEnabled);
    changeMagicLoginPermission({ params: { enabled: nextEnabled } });
  };

  return (
    <div
      className="flex flex-col gap-6 md:min-w-[464px]"
      data-testid="magic-link-login-setting"
    >
      <div className="flex flex-col gap-2">
        <h3 className="font-semibold text-secondary-100">
          {t("user-account.security.magicLink.title")}
        </h3>
        <p className="text-base">
          {t("user-account.security.magicLink.description")}
        </p>
      </div>

      <div
        className={isDisabled ? "pointer-events-none opacity-50" : undefined}
      >
        {(isLoadingCustomer || isUpdating) && <LoadingSpinner />}
        <div className="setting-grid-container">
          <div className="title">
            {t("user-account.security.magicLink.toggleTitle")}
          </div>
          <div className="toggle">
            <Toggle enabled={isEnabled} onChange={handleToggle} />
          </div>
          <div className="subtitle">
            {t("user-account.security.magicLink.toggleSubtitle")}
          </div>
        </div>
      </div>
    </div>
  );
};
